import type { DrawerPullStyle } from './blissRoomFinish.js'
import { finishedUpgradePrice } from './blissUpgradePricing.js'

/** Supplier material inputs checked 2026-09-29. USD, before tax/freight.
 * Pack stock is allocated by pieces used across jobs, not charged in full per room.
 * These are purchased components, not existing Bliss finished retail prices.
 */
export const CUSTOM_DETAIL_MATERIALS = {
  cap: {stockInches:96, unitCost:15.25, url:'https://www.homedepot.com/p/206201564', sku:'HLPO10308X'},
  quarterRound: {stockInches:96, unitCost:60.88/12, url:'https://www.homedepot.com/p/330745901', sku:'0W105-93096PK'},
  quarterRoundRaw: {stockInches:96, unitCost:76.31/12, url:'https://www.homedepot.com/p/330745899', sku:'0W105-20096PK'},
  gold_bar: {unitCost:14.40, url:'https://www.homedepot.com/p/342413768', sku:'RL006467'},
  silver_bar: {unitCost:9.89, url:'https://www.homedepot.com/p/310771263', sku:'RL062548'},
  rope: {unitCost:59.99/6, url:'https://vxb.com/products/nautical-twisted-jute-rope-drawer-pulls-with-brush', sku:'VXB0D1R4XSQK'},
} as const

/** First-fit decreasing stock allocation; ⅛" saw kerf per cut, no invented waste %. */
export function trimStockCount(cuts:number[], stock=96):number {
  const remaining:number[]=[]
  for(const length of [...cuts].sort((a,b)=>b-a)) {
    if(!Number.isFinite(length)||length<=0||length>stock) throw new Error('Custom trim stock length needs designer review.')
    const required=length===stock?stock:length+0.125
    const index=remaining.findIndex(n=>n>=required)
    if(index<0)remaining.push(stock-required)
    else remaining[index]-=required
  }
  return remaining.length
}
export function detailMaterialQuote(capCuts:number[], quarterCuts:number[], pull:DrawerPullStyle|undefined, drawers:number, unfinishedTrim=false) {
  if(!Number.isInteger(drawers)||drawers<0)throw new Error('Drawer quantity needs designer review.')
  const capOrder=trimOrderingQuantity(capCuts), quarterOrder=trimOrderingQuantity(quarterCuts)
  const capBoards=capOrder.boards,quarterBoards=quarterOrder.boards
  const capMaterials=capBoards*CUSTOM_DETAIL_MATERIALS.cap.unitCost
  const quarterMaterials=quarterBoards*(unfinishedTrim?CUSTOM_DETAIL_MATERIALS.quarterRoundRaw:CUSTOM_DETAIL_MATERIALS.quarterRound).unitCost
  const pullMaterials=pull&&pull!=='cutout'?drawers*CUSTOM_DETAIL_MATERIALS[pull].unitCost:0
  const materials=Math.round((capMaterials+quarterMaterials+pullMaterials)*100)/100
  const orderingCode = [
    'BC',
    ...(capBoards ? [`T13-${capOrder.orderLinearFeet}LF-${capBoards}X8`] : []),
    ...(quarterBoards ? [`QR34-${quarterOrder.orderLinearFeet}LF-${quarterBoards}X8${unfinishedTrim?'U':'P'}`] : []),
    ...(pull ? [`${{cutout:'HOLE',gold_bar:'GB',silver_bar:'SB',rope:'RP'}[pull]}-${drawers}`] : []),
    'A05',
  ].join('-')
  return {capBoards,quarterBoards,capOrder,quarterOrder,orderingCode,drawers,materials,
    materialBreakdown:{cap:Math.round(capMaterials*100)/100,quarterRound:Math.round(quarterMaterials*100)/100,pulls:Math.round(pullMaterials*100)/100},
    total:finishedUpgradePrice(materials)}
}

export const TRIM_MATERIAL_ALLOWANCE = 0.05
/** Purchasing only: preserve actual cut dimensions and take the greater of the
 * feasible cutting layout and 105% net footage plus saw kerf in whole stock.
 * Offcuts remain included in stock cost, not billed twice as an extra 5%.
 */
export function trimOrderingQuantity(cuts:number[]) {
  const fittedBoards=trimStockCount(cuts)
  const netInches=cuts.reduce((sum,n)=>sum+n,0)
  const allowanceInches=netInches*TRIM_MATERIAL_ALLOWANCE
  const requiredInches=netInches+allowanceInches
  const kerfInches=cuts.filter(n=>n!==96).length*0.125
  const boards=Math.max(fittedBoards,Math.ceil((requiredInches+kerfInches)/96))
  return {
    netLinearFeet:netInches/12,
    allowanceLinearFeet:allowanceInches/12,
    requiredLinearFeet:requiredInches/12,
    kerfInches,
    boards,
    stockLengthFeet:8,
    orderLinearFeet:boards*8,
  }
}
